import React from 'react';
import { 
  Compass, Scale, AlertTriangle, CheckCircle, 
  Users, FileWarning, GitMerge, Target, ListOrdered, Zap,
  Brain, X, Activity
} from 'lucide-react';
import { Panel, ValueBadge, GenerativeScript, StrategicFeedbackKQI, SynthesizedAlignment } from './CandidateShared';

// --- SUB-COMPONENT: Hybrid Intelligence Summary (Optimized UX) ---
const HybridIntelligencePanel = ({ data, wardName }) => {
  // Clear error state if data is missing for the selected ward
  if (!data) {
    return (
      <Panel title="Strategic Intelligence Summary" icon={Activity}>
        <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-lg flex items-center space-x-3">
          <FileWarning className="text-red-400" size={24} />
          <div>
            <strong className="text-red-400 text-sm">Intelligence Unavailable</strong>
            <p className="text-xs text-red-300">No hybrid analysis data found for {wardName}.</p>
          </div>
        </div>
      </Panel>
    );
  }

  // Safe data extraction from the hybrid_ward_analysis object
  const scores = data.component_scores || {};
  const metrics = data.resonance_metrics || {};
  
  // Formatters
  const fmt = (n) => (n !== undefined && n !== null) ? `${(n * 100).toFixed(1)}%` : 'N/A';
  const fmt10 = (n) => (n !== undefined && n !== null) ? (n * 10).toFixed(1) : '-';

  return (
    <Panel title="Strategic Intelligence Summary" icon={Activity} defaultOpen={true}>
      <div className="space-y-6">
        
        {/* PRIMARY METRIC: Cultural Fit Score */}
        <div className="bg-slate-900/60 p-5 rounded-lg border border-slate-700 shadow-inner">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">Cultural Fit Score</h4>
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-mono font-bold text-white">{fmt(data.final_hybrid_score)}</span>
                <span className="text-xs text-slate-500 font-medium">Weighted Alignment</span>
              </div>
            </div>
            <div className="text-right">
              <ValueBadge value={data.compatibility_level} />
              <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-tighter">Minerva Consensus v5.3</p>
            </div>
          </div>
        </div>

        {/* COMPONENT BREAKDOWN: ANNA (Demographics) vs THEIA (Psychology) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-slate-800/80 p-4 rounded-lg border-t-2 border-blue-500/50 transition-all hover:bg-slate-800">
            <div className="flex items-center space-x-2 mb-2 text-blue-400">
              <Users size={14} />
              <span className="text-[10px] font-bold uppercase tracking-wider">Demographic</span>
            </div>
            <div className="text-2xl font-mono font-bold text-white">{fmt(scores.anna_micro_score)}</div>
            <p className="text-[10px] text-slate-500 mt-1 uppercase">Census-based fit</p>
          </div>

          <div className="bg-slate-800/80 p-4 rounded-lg border-t-2 border-purple-500/50 transition-all hover:bg-slate-800">
            <div className="flex items-center space-x-2 mb-2 text-purple-400">
              <Brain size={14} />
              <span className="text-[10px] font-bold uppercase tracking-wider">Psychological</span>
            </div>
            <div className="text-2xl font-mono font-bold text-white">{fmt(scores.theia_macro_score)}</div>
            <p className="text-[10px] text-slate-500 mt-1 uppercase">Moral alignment</p>
          </div>
        </div>

        {/* RESONANCE DIAGNOSTICS SPECTRUM */}
        <div className="pt-2">
          <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center">
             Diagnostics Spectrum
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            
            {[
              { label: 'Activation Potential', val: metrics.activation_potential, color: 'bg-green-500' },
              { label: 'Cognitive Friction', val: metrics.cognitive_friction, color: 'bg-yellow-500' },
              { label: 'Messenger Credibility', val: metrics.messenger_credibility, color: 'bg-blue-500' },
              { label: 'Moral Authenticity', val: metrics.moral_authenticity, color: 'bg-purple-500' },
              { label: 'Polarization Index', val: metrics.polarization_index, color: 'bg-red-500' },
              { label: 'Resilience Match', val: metrics.resilience_match, color: 'bg-teal-500' },
            ].map((m) => (
              <div key={m.label} className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-bold">
                  <span className="text-slate-400 uppercase tracking-tight">{m.label}</span>
                  <span className="text-white font-mono">{fmt10(m.val)}/10</span>
                </div>
                <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${m.color} transition-all duration-1000`} 
                    style={{ width: `${(m.val || 0) * 100}%` }} 
                  />
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>
    </Panel>
  );
};

// --- SUB-COMPONENT: Coalition Analysis ---
const CoalitionAnalysis = ({ generalFit }) => {
  if (!generalFit) return <p className="text-slate-400 text-sm">No coalition data available.</p>;

  const naturalBase = generalFit.top_3_natural_fits || [];
  const deadZones = generalFit.bottom_3_natural_fits || [];

  return (
    <div className="space-y-6">
      {/* NATURAL BASE */}
      <div className="bg-slate-700/30 p-4 rounded-lg border-l-4 border-green-500">
        <h5 className="text-green-400 font-bold text-xs uppercase tracking-wider mb-3 flex items-center">
          <Users size={14} className="mr-2" /> Natural Base (High Resonance)
        </h5>
        <div className="space-y-4">
          {naturalBase.map((arch) => (
            <div key={arch.archetype_key} className="pb-3 border-b border-slate-700/50 last:border-0 last:pb-0">
              <div className="flex justify-between items-center mb-1">
                <span className="text-white font-semibold text-sm">{arch.archetype_name}</span>
                <span className="text-green-400 font-bold text-xs">{(arch.general_fit_score * 100).toFixed(0)}% Fit</span>
              </div>
              <p className="text-xs text-slate-400 mb-2">{arch.description}</p>
              <span className="inline-block text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded border border-green-500/20">
                Segment: {arch.main_segment}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* DEAD ZONES */}
      <div className="bg-slate-700/30 p-4 rounded-lg border-l-4 border-red-500">
        <h5 className="text-red-400 font-bold text-xs uppercase tracking-wider mb-3 flex items-center">
          <X size={14} className="mr-2" /> Dead Zones (Low Resonance)
        </h5>
        <div className="space-y-4">
          {deadZones.map((arch) => (
            <div key={arch.archetype_key} className="pb-3 border-b border-slate-700/50 last:border-0 last:pb-0">
              <div className="flex justify-between items-center mb-1">
                <span className="text-white font-semibold text-sm">{arch.archetype_name}</span>
                <span className="text-red-400 font-bold text-xs">{(arch.general_fit_score * 100).toFixed(0)}% Fit</span>
              </div>
              <p className="text-xs text-slate-400 mb-2">{arch.description}</p>
              <span className="inline-block text-[10px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded border border-red-500/20">
                Gap: Moral Mismatch
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT: Electorate Tab ---
const ElectorateTab = ({
  selectedWardCode,
  selectedWardName,
  selectedWardStrategy,
  selectedWardRisk,
  selectedWardTheiaScore,
  selectedWardNarrative,
  profile,
  theiaElectorateAnalysis,
  authenticityGapAnalysis,
  compatibility,
  generalArchetypeFit,
  hybridWardAnalysis
}) => {
  
  // Find the hybrid data specifically for the currently selected ward
  const currentHybrid = (hybridWardAnalysis || []).find(h => h.ward_code === selectedWardCode);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* LEFT CONTENT AREA */}
      <div className="lg:col-span-2 space-y-8">
        
        {selectedWardCode && selectedWardName ? (
            <>
              {/* 1. STRATEGIC APPROACH */}
              {selectedWardStrategy?.approach && (
              <Panel title={`Strategic Approach: ${selectedWardName}`} icon={Compass}>
                <p className="text-slate-300 text-sm leading-relaxed">{selectedWardStrategy.approach}</p>
              </Panel>
              )}

              {/* 2. OPTIMIZED HYBRID INTELLIGENCE SUMMARY */}
              <HybridIntelligencePanel data={currentHybrid} wardName={selectedWardName} />

              {/* 3. CANDIDATE STATE & COGNITIVE LOAD */}
              <Panel title="Candidate State (Simulated)" icon={Brain}>
                {(() => {
                  const authRisk = (selectedWardRisk?.risks_identified || []).find(
                    r => r.source === 'MINERVA (THEIA)' && r.risk.includes('Authenticity Gap')
                  );
                  const energy = profile.resilience_profile?.energy_sustainability || 0;
                  
                  if (authRisk) {
                    return (
                      <div className="flex items-start space-x-3">
                        <AlertTriangle size={16} className="text-yellow-400 mt-1 flex-shrink-0" />
                        <div>
                          <strong className="text-yellow-400 text-sm font-bold">High Cognitive Load</strong>
                          <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                            Playbook execution requires an "Unnatural" pivot against core drivers. Expect rapid depletion of energy reserves (Current Sustainability: <strong className="text-white">{energy ? (energy * 100).toFixed(0) + '%' : 'N/A'}</strong>).
                          </p>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                        <div className="flex items-start space-x-3">
                        <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                        <div>
                          <strong className="text-green-400 text-sm font-bold">Low Cognitive Load</strong>
                          <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                            Playbook aligns with natural drivers. This is an energy-generating event reinforcing authenticity.
                          </p>
                        </div>
                      </div>
                    );
                  }
                })()}
              </Panel>

              {/* 4. DETAILED WARD ANALYSIS (THEIA) */}
              <Panel title={`Ward Electorate Analysis: ${selectedWardName}`} icon={Scale}>
                {selectedWardTheiaScore ? (
                  <div className="space-y-4">
                    <div className="mb-4 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                      <h4 className="font-semibold text-white mb-2 text-sm">THEIA Electorate Fit</h4>
                      <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
                        <ValueBadge value={selectedWardTheiaScore.compatibility_level} />
                        <span className="text-sm text-slate-400">Score: <strong className="text-white">{(selectedWardTheiaScore.theia_compatibility_score * 100).toFixed(1)}%</strong></span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-white mb-2 flex items-center text-sm"><AlertTriangle size={16} className="mr-2 text-red-400" /> Authenticity Gaps</h4>
                      <ul className="space-y-3">
                        {(selectedWardRisk?.risks_identified || []).filter(r => r.source === 'MINERVA (THEIA)' && r.risk.includes('Authenticity Gap')).map((risk, i) => (
                            <li key={i} className="flex items-start space-x-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                              <AlertTriangle size={16} className="text-red-400 mt-1 flex-shrink-0" />
                              <div>
                                <strong className="text-red-400 text-xs">Gap: {risk.risk}</strong>
                                <p className="text-xs text-slate-400 mt-1">{risk.details}</p>
                                <p className="text-xs text-green-400 mt-2"><strong>Pivot:</strong> {risk.recommendation}</p>
                              </div>
                            </li>
                          ))
                        }
                        {(selectedWardRisk?.risks_identified || []).filter(r => r.source === 'MINERVA (THEIA)').length === 0 && (
                          <li className="flex items-start space-x-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                            <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                            <div><strong className="text-green-400 text-xs">No Gaps Identified</strong></div>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                ) : <p className="text-slate-500 text-sm">No detailed THEIA analysis available for this ward.</p>}
              </Panel>

              {/* 5. RISK MATRIX */}
              <Panel title={`Contextual Risk Matrix: ${selectedWardName}`} icon={FileWarning}>
                {selectedWardRisk ? (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-white mb-2 flex items-center text-sm"><Scale size={16} className="mr-2 text-red-400" /> Authenticity Risks</h4>
                      <ul className="list-disc list-inside space-y-1 text-xs text-slate-300 pl-4">
                        {(selectedWardRisk.risks_identified || []).filter(r => r.risk.includes('Authenticity')).map((risk, i) => <li key={i}>{risk.details}</li>)}
                      </ul>
                    </div>
                    <div className="pt-4 border-t border-slate-700">
                      <h4 className="font-semibold text-white mb-2 flex items-center text-sm"><Users size={16} className="mr-2 text-yellow-400" /> Voter & Landscape Risks</h4>
                      <ul className="list-disc list-inside space-y-1 text-xs text-slate-300 pl-4">
                        {(selectedWardRisk.risks_identified || []).filter(r => !r.risk.includes('Authenticity')).map((risk, i) => (
                          <li key={i}>
                            <strong>[{risk.source}]</strong> {risk.risk}: <span className="text-slate-400">{risk.details}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : <p className="text-slate-500 text-sm">No specific risks identified.</p>}
              </Panel>

              {/* 6. PLAYBOOK & FEEDBACK */}
              <Panel title={`Actionable Playbook: ${selectedWardName}`} icon={ListOrdered}>
                  {selectedWardNarrative ? (
                    <div className="space-y-4">
                      <div className="mb-2"><ValueBadge value={selectedWardNarrative.candidate_compatibility_level} /></div>
                      <div>
                        <h4 className="font-semibold text-white mb-2 text-sm">Recommended Angles</h4>
                        <ul className="list-disc list-inside space-y-1 text-xs text-slate-300 pl-4">
                          {(selectedWardNarrative.recommended_angles || []).map((angle, i) => <li key={i}>{angle}</li>)}
                        </ul>
                      </div>
                      <GenerativeScript wardRisk={selectedWardRisk} candidateProfile={profile} />
                      <StrategicFeedbackKQI wardRisk={selectedWardRisk} />
                    </div>
                  ) : <p className="text-slate-500 text-sm">No specific messaging available for this ward.</p>}
              </Panel>
            </>
        ) : (
            <div className="flex items-center justify-center h-48 rounded-lg bg-slate-800/50 border border-slate-700 border-dashed">
              <div className="text-center text-slate-500">
                <Compass className="mx-auto mb-2 opacity-50" size={32} />
                <p className="text-sm">Select a ward from the Command Center above to view detailed Electorate Analysis.</p>
              </div>
            </div>
        )}

        {/* BOTTOM: ALIGNMENT SUMMARY */}
        <Panel title="Electorate Alignment Summary" icon={GitMerge}>
          <SynthesizedAlignment theiaAnalysis={theiaElectorateAnalysis} gapAnalysis={authenticityGapAnalysis} />
        </Panel>
      </div>

      {/* RIGHT SIDEBAR */}
      <aside className="space-y-8">
          
          {/* COALITION ANALYSIS */}
          <Panel title="Natural Voter Coalition" icon={Target}>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Voter segments with the highest *natural* resonance vs. "Dead Zones" where authenticity is impossible.
            </p>
            <CoalitionAnalysis generalFit={generalArchetypeFit} />
          </Panel>

          {/* LIST OF ARCHETYPES */}
          <Panel title="Natural Electorate Archetypes" icon={ListOrdered}>
            <p className="text-xs text-slate-500 mb-4">Raw list of compatible voter segments.</p>
            <ul className="space-y-4">
              {(compatibility.archetype_scores || []).map((arch, i) => (
                <li key={i} className="text-xs border-l-2 border-green-500 pl-3 transition-all hover:bg-slate-700/20 py-1">
                  <strong className="text-white block">{arch.archetype_name}</strong>
                  <span className="text-slate-400 block mb-1">{arch.archetype_category}</span>
                  <div className="mt-1"><ValueBadge value={arch.compatibility_level.split(' - ')[0]} /></div>
                </li>
              ))}
            </ul>
          </Panel>
      </aside>
    </div>
  );
};

export default ElectorateTab;